// @spec POL-STR-002
'use client';

import { createFileRoute, Outlet, useMatch } from '@tanstack/react-router';

import { PolicyDetailPage, usePolicy } from '@/features/policies';
import i18n from '@/lib/i18n';

export const Route = createFileRoute('/_authenticated/policies/$policyId')({
  beforeLoad: () => ({
    getTitle: () => i18n.t('routeTitles.policyDetails'),
  }),
  component: PolicyDetailRoute,
});

/**
 * Policy detail route - Full policy view.
 *
 * Displays complete policy information including:
 * - Title, status, and type
 * - Full content with table of contents
 * - Metadata (owner, dates, review schedule)
 * - Compliance framework mappings
 * - Related documents and procedures
 *
 * Also serves as a layout for child routes (edit, review) by rendering Outlet.
 */
function PolicyDetailRoute() {
  const { policyId } = Route.useParams();

  // Check if we're on a child route (edit, review, scans)
  const editMatch = useMatch({
    from: '/_authenticated/policies/$policyId/edit',
    shouldThrow: false,
  });
  const reviewMatch = useMatch({
    from: '/_authenticated/policies/$policyId/review',
    shouldThrow: false,
  });
  const scanResultsMatch = useMatch({
    from: '/_authenticated/policies/$policyId/scans/$scanId',
    shouldThrow: false,
  });
  // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing -- intentional truthiness check for match objects
  const hasChildRoute = editMatch || reviewMatch || scanResultsMatch;

  const { policy, isLoading, isError, error, refetch } = usePolicy({ policyId });

  // If on a child route, render the child content via Outlet
  if (hasChildRoute) {
    return <Outlet />;
  }

  return (
    <PolicyDetailPage
      policy={policy}
      isLoading={isLoading}
      isError={isError}
      error={error}
      refetch={refetch}
    />
  );
}
